import React, { useState, useEffect } from 'react';
import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ModalInfo } from '../../../components/ModalInfo';
import { sendWarehouseLowStockEmail } from '../../../api';

const WarehouseQuantityModal = ({ isOpen, onClose, item, actionType, onConfirm }) => {
  const [amount, setAmount] = useState(0);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isResultOpen, setIsResultOpen] = useState(false);
  const [resultMessage, setResultMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      setAmount(0);
      setIsConfirmOpen(false);
      setIsResultOpen(false);
      setResultMessage('');
    }
  }, [isOpen]);

  if (!item) return null;

  const numericAmount = Number(amount);
  const isAmountValid = Number.isFinite(numericAmount) && numericAmount > 0;
  const isPrelievoInvalid =
    actionType === 'prelievo' && isAmountValid && numericAmount > item.quantity;
  const finalQuantity = isAmountValid
    ? actionType === 'deposito'
      ? item.quantity + numericAmount
      : item.quantity - numericAmount
    : null;
  const willBeBelowMin =
    actionType === 'prelievo' &&
    isAmountValid &&
    item.minQuantity > 0 &&
    finalQuantity !== null &&
    finalQuantity <= item.minQuantity;
  const canConfirm = isAmountValid && !isPrelievoInvalid;

  const handleOpenConfirm = (event) => {
    event.preventDefault();
    if (!canConfirm) return;
    setIsConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    try {
      const delta = actionType === 'deposito' ? numericAmount : -numericAmount;
      await onConfirm(item, delta, actionType);
      
      // Se dopo il prelievo la quantità è <= soglia minima, invia mail agli admin
      // console.log('willBeBelowMin', willBeBelowMin);
      if (willBeBelowMin) {
        try {
          await sendWarehouseLowStockEmail(item.name, finalQuantity, item.minQuantity);
        } catch (emailError) {
          console.error('Errore nell\'invio della mail di avviso:', emailError);
        }
      }
      
      const azioneLabel = actionType === 'deposito' ? 'deposito' : 'prelievo';
      setResultMessage(
        `Operazione di ${azioneLabel} di ${numericAmount} sul prodotto "${item.name}" completata con successo.`,
      );
    } finally {
      setIsConfirmOpen(false);
      setIsResultOpen(true);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg='#2d3748' color='white'>
          <ModalHeader>Movimento di magazzino</ModalHeader>
          <ModalCloseButton />
          <ModalBody as='form' onSubmit={handleOpenConfirm}>
            <VStack align='stretch' spacing={5}>
              <Text fontSize='lg'>
                Prodotto: <strong>{item.name}</strong>
              </Text>
              <Text fontSize='lg'>Quantità attuale: {item.quantity}</Text>

              <FormControl>
                <FormLabel fontSize='lg'>Azione</FormLabel>
                <Text
                  fontSize='lg'
                  fontWeight='bold'
                  color={actionType === 'deposito' ? 'green.300' : 'red.300'}
                >
                  {actionType === 'deposito' ? 'Deposito' : 'Prelievo'}
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel fontSize='lg'>Quantità</FormLabel>
                <NumberInput min={1}>
                  <NumberInputField
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </NumberInput>
              </FormControl>

              {isAmountValid && (
                <Text fontSize='lg'>
                  Quantità finale:{' '}
                  <strong>{finalQuantity}</strong>
                </Text>
              )}

              {willBeBelowMin && (
                <Alert status='warning' borderRadius='md'>
                  <AlertIcon />
                  <Text fontSize='md'>
                    Dopo questo prelievo il prodotto sarà sotto la soglia minima, verrà mandata una email a tutti gli admin.
                  </Text>
                </Alert>
              )}

              {isPrelievoInvalid && (
                <Text fontSize='md' color='red.300'>
                  Il prelievo non può superare la quantità disponibile.
                </Text>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onClose}   variant='secondary'>
              Annulla
            </Button>
            <Button onClick={handleOpenConfirm} isDisabled={!canConfirm}>
              Conferma
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ModalInfo
        title='Conferma movimento'
        body={
          isConfirmOpen && item
            ? `Confermi ${
                actionType === 'deposito' ? 'il deposito' : 'il prelievo'
              } di ${Number(amount)} unità per il prodotto "${item.name}"?`
            : ''
        }
        isOpen={isConfirmOpen}
        onCancel={() => setIsConfirmOpen(false)}
        onClose={handleConfirm}
      />

      <ModalInfo
        title='Risultato movimento'
        body={resultMessage}
        isOpen={isResultOpen}
        onClose={() => {
          setIsResultOpen(false);
          onClose();
        }}
      />
    </>
  );
};

export default WarehouseQuantityModal;

